import {
  CButton,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from '@coreui/react-pro'
import { useHistory } from 'react-router-dom'
import React, { useRef, useState } from 'react'
import { useAppDispatch, useTypedSelector } from '../stateStore'
import { reduxServices } from '../reducers/reduxServices'
import { ApiLoadingState } from '../middleware/api/apiList'

interface ChangePasswordModalProps {
  visible: boolean
  onClose: () => void
}

interface ValidationMessagesProps {
  noCredentials: boolean
  noPassword: boolean
  noUsername: boolean
  noConfirmPassword: boolean | string
  passwordNotMatched: boolean | string
  localError: string
  successMessage: string
  passwordValidationError?: string
}

const ValidationMessages: React.FC<ValidationMessagesProps> = ({
  noCredentials,
  noPassword,
  noUsername,
  noConfirmPassword,
  passwordNotMatched,
  localError,
  successMessage,
  passwordValidationError,
}) => (
  <>
    {noCredentials && (
      <p className="text-danger text-start mb-0">
        Username and password is missing.
      </p>
    )}
    {!noCredentials && noPassword && (
      <p className="text-danger text-start mb-0">Password is missing.</p>
    )}
    {!noCredentials && noUsername && (
      <p className="text-danger text-start mb-0">Username is missing.</p>
    )}
    {passwordValidationError && (
      <p className="text-danger text-start mb-0">{passwordValidationError}</p>
    )}
    {noConfirmPassword && (
      <p className="text-danger text-start mb-0">
        Confirm password is missing.
      </p>
    )}
    {passwordNotMatched && (
      <p className="text-danger text-start mb-0">
        New password and confirm password do not match.
      </p>
    )}
    {localError && <p className="text-danger text-start mb-0">{localError}</p>}
    {successMessage && (
      <p className="text-success text-start mb-0">{successMessage}</p>
    )}
  </>
)

const ChangePasswordModal = ({
  visible,
  onClose,
}: ChangePasswordModalProps): JSX.Element => {
  const usernameRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const confirmPasswordRef = useRef<HTMLInputElement>(null)

  const vendorUserName = localStorage.getItem('userName') || ''
  const [username, setUsername] = useState(vendorUserName)
  const [newPassword, setNewPassword] = useState('')

  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [loader, setLoader] = useState(false)
  const [localError, setLocalError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [passwordValidationError, setPasswordValidationError] = useState('')

  const history = useHistory()
  const dispatch = useAppDispatch()
  const loading = useTypedSelector(
    reduxServices.authentication.selectors.selectIsLoading,
  )

  const passwordNotMatched =
    hasSubmitted &&
    newPassword &&
    confirmPassword &&
    newPassword !== confirmPassword

  const tenantKey = localStorage.getItem('tenantKey') || ''
  const errormsg = 'Unable to update password. Please try again.'
  const noConfirmPassword = hasSubmitted && newPassword && !confirmPassword
  const noUsername = hasSubmitted && !username
  const noPassword = hasSubmitted && !newPassword
  const noCredentials = hasSubmitted && !username && !newPassword

  const validatePasswordStrength = (password: string): boolean => {
    if (!password) {
      setPasswordValidationError('Password is required')
      return false
    }
    if (password.length < 8) {
      setPasswordValidationError('Password must be at least 8 characters')
      return false
    }

    const uppercaseRegex = /[A-Z]/
    if (!uppercaseRegex.test(password)) {
      setPasswordValidationError(
        'Password must contain at least one uppercase letter',
      )
      return false
    }

    const lowercaseRegex = /[a-z]/
    if (!lowercaseRegex.test(password)) {
      setPasswordValidationError(
        'Password must contain at least one lowercase letter',
      )
      return false
    }

    const specialCharRegex = /[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]/
    if (!specialCharRegex.test(password)) {
      setPasswordValidationError(
        'Password must contain at least one special character',
      )
      return false
    }

    setPasswordValidationError('')
    return true
  }

  const validateInputs = (): boolean => {
    if (!username) {
      usernameRef.current?.focus()
      return false
    }
    if (!newPassword) {
      passwordRef.current?.focus()
      return false
    }
    if (!validatePasswordStrength(newPassword)) {
      passwordRef.current?.focus()
      return false
    }
    if (!confirmPassword) {
      confirmPasswordRef.current?.focus()
      return false
    }

    return Boolean(
      username &&
        newPassword &&
        confirmPassword &&
        newPassword === confirmPassword,
    )
  }

  const handlePasswordResetSuccess = () => {
    setSuccessMessage('Password changed successfully!')
    setUsername('')
    setNewPassword('')
    setConfirmPassword('')
    setHasSubmitted(false)

    setTimeout(() => {
      onClose()
      setSuccessMessage('')
      history.push('/vendorLogin')
    }, 1000)
  }

  const handleResetPassword = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    setHasSubmitted(true)
    setLocalError('')
    setSuccessMessage('')

    if (!validateInputs()) return

    try {
      setLoader(true)
      const resultAction = await dispatch(
        reduxServices.VendorReset.authenticateUser({
          username,
          newPassword,
          tenantKey,
        }),
      )

      if (
        reduxServices.VendorReset.authenticateUser.fulfilled.match(resultAction)
      ) {
        handlePasswordResetSuccess()
      } else if (
        reduxServices.VendorReset.authenticateUser.rejected.match(resultAction)
      ) {
        setLocalError(errormsg)
      }
    } catch (err) {
      setLocalError('Something went wrong. Please try again.')
    } finally {
      setLoader(false)
    }
  }

  //   const validatePassword = (
  //     password: string,
  //     setPasswordError: (error: string) => void,
  //   ): boolean => {
  //     if (!password) {
  //       setPasswordError('Password is required')
  //       return false
  //     }
  //     if (password.length < 8) {
  //       setPasswordError('Password must be at least 8 characters')
  //       return false
  //     }
  //     if (password.length > 20) {
  //       setPasswordError('Password must not exceed 20 characters')
  //       return false
  //     }
  //     const specialCharRegex = /[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?]/
  //     if (!specialCharRegex.test(password)) {
  //       setPasswordError('Password must contain at least one special character')
  //       return false
  //     }
  //     setPasswordError('')
  //     return specialCharRegex.test(password)
  //   }

  const handleClose = () => {
    setUsername(vendorUserName)
    setNewPassword('')
    setConfirmPassword('')
    setHasSubmitted(false)
    setLocalError('')
    setSuccessMessage('')
    setPasswordValidationError('')
    setShowNewPassword(false)
    setShowConfirmPassword(false)
    onClose()
  }

  return (
    <CModal visible={visible} onClose={handleClose} alignment="center">
      <CModalHeader closeButton>
        <CModalTitle>Change Password</CModalTitle>
      </CModalHeader>
      <CModalBody className="px-4 pt-2 pb-4">
        <CForm>
          <CInputGroup className="mb-3">
            <CInputGroupText>
              <i className="fa fa-user"></i>
            </CInputGroupText>
            <CFormInput
              ref={usernameRef}
              placeholder="Username"
              size="sm"
              disabled={username !== ''}
              value={username}
              onChange={(e) => {
                setHasSubmitted(false)
                setLocalError('')
                setUsername(e.target.value)
              }}
              autoFocus
            />
          </CInputGroup>

          <CInputGroup className="mb-3">
            <CInputGroupText>
              <i className="fa fa-asterisk"></i>
            </CInputGroupText>
            <CFormInput
              ref={passwordRef}
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New Password"
              size="sm"
              value={newPassword}
              onChange={(e) => {
                setHasSubmitted(false)
                setLocalError('')
                setPasswordValidationError('')
                setNewPassword(e.target.value)
              }}
              disabled={loading === ApiLoadingState.loading}
            />
            <CInputGroupText
              onClick={() => setShowNewPassword((prev) => !prev)}
              style={{ cursor: 'pointer' }}
            >
              <i
                className={`fa ${showNewPassword ? 'fa-eye' : 'fa-eye-slash'}`}
              />
            </CInputGroupText>
          </CInputGroup>

          <CInputGroup className="mb-3">
            <CInputGroupText>
              <i className="fa fa-asterisk"></i>
            </CInputGroupText>
            <CFormInput
              ref={confirmPasswordRef}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm Password"
              size="sm"
              value={confirmPassword}
              onChange={(e) => {
                setHasSubmitted(false)
                setLocalError('')
                setConfirmPassword(e.target.value)
              }}
              disabled={loading === ApiLoadingState.loading}
            />
            <CInputGroupText
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              style={{ cursor: 'pointer' }}
            >
              <i
                className={`fa ${
                  showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'
                }`}
              />
            </CInputGroupText>
          </CInputGroup>

          <div className="d-grid mb-0">
            <CButton
              color="primary"
              size="sm"
              onClick={handleResetPassword}
              disabled={loader}
              type="submit"
            >
              {loader ? <CSpinner size="sm" /> : 'Submit'}
            </CButton>
          </div>

          <ValidationMessages
            noCredentials={noCredentials}
            noPassword={noPassword}
            noUsername={noUsername}
            noConfirmPassword={noConfirmPassword}
            passwordNotMatched={passwordNotMatched}
            localError={localError}
            successMessage={successMessage}
            passwordValidationError={passwordValidationError}
          />
        </CForm>
      </CModalBody>
    </CModal>
  )
}

export default React.memo(ChangePasswordModal)
